import type {
  BossId,
  CatalogEntry,
  PlayerRole,
  PublicPlayer,
  PublicRoom,
} from '@bioforge/protocol';
import { Container, Graphics, Text } from 'pixi.js';
import { ABILITY_NAMES, BOSS_NAMES, BOSS_TITLES, DUEL_ABILITY_HINTS } from '../labels.js';
import { tr } from '../story/story.js';
import { idle } from '../ui/motion.js';
import { artSprite } from '../ui/textures.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { UiButton, panel, vertical } from '../ui/widgets.js';
import { caps } from './battle-panels.js';

/** Лобби комнаты (SPEC.md, раздел 16): код и приглашение, игроки, готовность, способности или роли. */

export interface LobbyData {
  readonly room: PublicRoom;
  readonly playerId: string | null;
  /** Ссылка-приглашение для второго игрока. */
  readonly inviteUrl: string;
  /** Выбранные способности Дуэли (слоты C и V). */
  readonly loadout: readonly ['cryo' | 'antibiotic', 'fever' | 'immunity'];
  readonly catalog: readonly CatalogEntry[];
  /** Секунд до начала матча (отсчёт); null — отсчёта нет. */
  readonly countdown: number | null;
}

export interface LobbyActions {
  readonly copyInvite: () => void;
  readonly setReady: (ready: boolean) => void;
  readonly selectRole: (role: PlayerRole) => void;
  readonly selectLoadout: (c: 'cryo' | 'antibiotic', v: 'fever' | 'immunity') => void;
}

const isBossId = (id: string | null): id is BossId => id !== null && id in BOSS_NAMES;

/** Раскладка лобби: на широком экране карточки рядом, на телефоне — одна под другой. */
interface LobbyLayout {
  readonly room: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
  readonly choice: {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  };
  /** Отступы внутри карточки комнаты: босс, заголовок игроков, место игрока, статус. */
  readonly bossY: number;
  readonly bossH: number;
  readonly playersY: number;
  readonly slotStep: number;
  readonly statusY: number;
  /** Ряды способностей, высота варианта и строка «Инфицирования». */
  readonly slotRowY: number;
  readonly slotRowStep: number;
  readonly optionH: number;
  readonly infectY: number;
  readonly roleH: number;
  readonly center: { readonly x: number; readonly y: number };
  readonly keys: boolean;
}

const WIDE: LobbyLayout = {
  room: { x: 60, y: 120, w: 560, h: 680 },
  choice: { x: 660, y: 120, w: 720, h: 680 },
  bossY: 214,
  bossH: 110,
  playersY: 350,
  slotStep: 96,
  statusY: 580,
  slotRowY: 60,
  slotRowStep: 210,
  optionH: 140,
  infectY: 480,
  roleH: 380,
  center: { x: 720, y: 430 },
  keys: true,
};

const TALL: LobbyLayout = {
  room: { x: 22, y: 96, w: 676, h: 560 },
  choice: { x: 22, y: 672, w: 676, h: 580 },
  bossY: 200,
  bossH: 96,
  playersY: 316,
  slotStep: 92,
  statusY: 524,
  slotRowY: 56,
  slotRowStep: 168,
  optionH: 120,
  infectY: 396,
  roleH: 330,
  center: { x: 360, y: 560 },
  keys: false,
};

export class LobbyView extends Container {
  private readonly stops: (() => void)[] = [];

  private readonly layout: LobbyLayout;

  constructor(
    private readonly actions: LobbyActions,
    portrait = false,
  ) {
    super();
    this.layout = portrait ? TALL : WIDE;
  }

  render(data: LobbyData): void {
    this.stops.splice(0).forEach((stop) => stop());
    this.removeChildren().forEach((child) => child.destroy({ children: true }));
    this.roomCard(data);
    this.choiceCard(data);
    if (data.countdown !== null) this.countdown(data.countdown);
  }

  /** Слева: код комнаты, приглашение, босс матча и игроки. */
  private roomCard(data: LobbyData): void {
    const { room } = data;
    const card = new Container();
    const { room: frame } = this.layout;
    card.position.set(frame.x, frame.y);
    const w = frame.w;
    card.addChild(panel({ width: w, height: frame.h, radius: 24, alpha: 0.94 }));
    const kicker = caps('Код комнаты');
    kicker.position.set(28, 28);
    const code = new Text({
      text: room.code,
      style: textStyle({ size: 60, display: true, weight: '900', shadow: 4, letterSpacing: 8 }),
    });
    code.position.set(28, 48);
    const copy = new UiButton({
      width: 230,
      height: 50,
      label: 'Скопировать ссылку',
      fontSize: 14,
      center: true,
      onTap: this.actions.copyInvite,
    });
    copy.position.set(28, 132);
    const hint = new Text({
      text: 'Отправьте код или ссылку второму игроку — он войдёт в эту комнату.',
      style: textStyle({ size: 14, color: COLOR.textSoft, wrap: w - 300, lineHeight: 19 }),
    });
    hint.position.set(278, 136);
    card.addChild(kicker, code, copy, hint);

    // Босс матча.
    const boss = new Container();
    boss.position.set(28, this.layout.bossY);
    boss.addChild(
      new Graphics()
        .roundRect(0, 0, w - 56, this.layout.bossH, 18)
        .fill({ color: 0x4a1638, alpha: isBossId(room.bossId) ? 0.6 : 0.25 })
        .stroke({ width: 2, color: 0x5e2346 }),
    );
    if (isBossId(room.bossId)) {
      const face = artSprite(`boss-${room.bossId}`, 88);
      face.position.set(14 + 44, this.layout.bossH / 2);
      this.stops.push(idle(face, 'boss'));
      const k = caps(room.mode === 'coop' ? 'Общий босс' : 'У каждого свой босс', COLOR.pink);
      k.position.set(118, this.layout.bossH / 2 - 31);
      const name = new Text({
        text: BOSS_NAMES[room.bossId],
        style: textStyle({ size: 24, display: true, weight: '900', shadow: 3 }),
      });
      name.position.set(118, this.layout.bossH / 2 - 13);
      const title = new Text({
        text: BOSS_TITLES[room.bossId],
        style: textStyle({ size: 13, color: 0xe2b8cc }),
      });
      title.position.set(118, this.layout.bossH / 2 + 21);
      boss.addChild(face, k, name, title);
    } else {
      const t = new Text({
        text:
          room.mode === 'coop'
            ? 'Классический матч: общая банка без босса'
            : 'Классический матч: кто первым очистит банку',
        style: textStyle({ size: 16, color: COLOR.textSoft, wrap: w - 100 }),
      });
      t.position.set(24, 42);
      boss.addChild(t);
    }
    card.addChild(boss);

    // Игроки.
    const playersTitle = caps('Игроки');
    playersTitle.position.set(28, this.layout.playersY);
    card.addChild(playersTitle);
    for (let i = 0; i < 2; i++) {
      const slot = this.playerSlot(room.players[i] ?? null, data, w - 56);
      slot.position.set(28, this.layout.playersY + 26 + i * this.layout.slotStep);
      card.addChild(slot);
    }

    const all = room.players.length === 2 && room.players.every((p) => p.ready && p.connected);
    const status = new Text({
      text:
        room.players.length < 2
          ? 'Ждём второго игрока…'
          : all
            ? 'Оба готовы — матч начинается!'
            : 'Матч начнётся, когда оба будут готовы.',
      style: textStyle({ size: 15, color: all ? COLOR.mint : COLOR.textSoft }),
    });
    status.position.set(28, this.layout.statusY);
    card.addChild(status);
    this.addChild(card);
  }

  private playerSlot(player: PublicPlayer | null, data: LobbyData, w: number): Container {
    const box = new Container();
    const me = player?.playerId === data.playerId;
    const g = new Graphics().roundRect(0, 0, w, 84, 16);
    if (!player) {
      g.fill({ color: COLOR.night, alpha: 0.35 }).stroke({
        width: 2,
        color: COLOR.border,
        alpha: 0.6,
      });
      const t = new Text({
        text: 'Свободное место',
        style: textStyle({ size: 16, color: COLOR.textMuted }),
      });
      t.anchor.set(0, 0.5);
      t.position.set(24, 42);
      box.addChild(g, t);
      return box;
    }
    g.fill(vertical(me ? 0x245678 : 0x1e3e63, 0x0e1c3a)).stroke({
      width: me ? 3 : 2,
      color: me ? COLOR.mint : COLOR.border,
    });
    const avatar = new Graphics()
      .circle(42, 42, 26)
      .fill(me ? COLOR.teal : 0xff4f7b)
      .stroke({ width: 3, color: COLOR.ink });
    const icon = artSprite('icon-user', 26);
    icon.position.set(42, 42);
    const name = new Text({
      text: me ? `${player.name} (вы)` : player.name,
      style: textStyle({ size: 17, display: true, weight: '800' }),
    });
    name.position.set(84, 16);
    const role =
      data.room.mode === 'coop'
        ? player.role === 'pilot'
          ? 'Пилот'
          : player.role === 'strategist'
            ? 'Стратег'
            : 'Роль не выбрана'
        : player.loadout
            .map((k) => ABILITY_NAMES[k as keyof typeof ABILITY_NAMES] ?? k)
            .join(' · ');
    const sub = new Text({ text: role, style: textStyle({ size: 13, color: COLOR.textSoft }) });
    sub.position.set(84, 48);
    const state = !player.connected ? 'Нет связи' : player.ready ? 'Готов' : 'Не готов';
    const tone = !player.connected ? COLOR.gold : player.ready ? COLOR.teal : COLOR.textMuted;
    const chipText = caps(state, player.ready && player.connected ? COLOR.ink : tone, 12);
    const chipW = chipText.width + 24;
    const chip = new Graphics().roundRect(w - 16 - chipW, 26, chipW, 32, 10);
    if (player.ready && player.connected) chip.fill(COLOR.teal);
    else chip.fill({ color: tone, alpha: 0.12 }).stroke({ width: 2, color: tone, alpha: 0.6 });
    chipText.anchor.set(0.5);
    chipText.position.set(w - 16 - chipW / 2, 42);
    box.addChild(g, avatar, icon, name, sub, chip, chipText);
    return box;
  }

  /** Справа: способности (Дуэль) или роли (Кооператив) и кнопка готовности. */
  private choiceCard(data: LobbyData): void {
    const card = new Container();
    const { choice: frame } = this.layout;
    card.position.set(frame.x, frame.y);
    const w = frame.w;
    const h = frame.h;
    card.addChild(panel({ width: w, height: h, radius: 24, alpha: 0.94 }));
    const me = data.room.players.find((p) => p.playerId === data.playerId) ?? null;
    if (data.room.mode === 'versus') this.loadoutChoice(card, data, w);
    else this.roleChoice(card, data, me, w);

    const ready = me?.ready ?? false;
    const button = new UiButton({
      width: w - 56,
      height: 64,
      label: ready ? 'Не готов' : 'Готов',
      variant: ready ? 'secondary' : 'primary',
      center: true,
      fontSize: 20,
      ...(ready ? {} : { icon: { key: 'icon-check', size: 22 } }),
      disabled: !me || (data.room.mode === 'coop' && !me.role),
      onTap: () => this.actions.setReady(!ready),
    });
    button.position.set(28, h - 28 - 64 - (this.layout.keys ? 26 : 0));
    const keys = new Text({
      text:
        data.room.mode === 'coop'
          ? 'P — Пилот · S — Стратег · Enter — готов · Esc — в меню'
          : 'C и V — сменить способности · Enter — готов · Esc — в меню',
      style: textStyle({ size: 13, color: COLOR.textMuted }),
    });
    keys.anchor.set(0.5, 0);
    keys.position.set(w / 2, h - 28 - 18);
    keys.visible = this.layout.keys;
    card.addChild(button, keys);
    this.addChild(card);
  }

  private loadoutChoice(card: Container, data: LobbyData, w: number): void {
    const title = caps('Способности на матч');
    title.position.set(28, 28);
    card.addChild(title);
    const [c, v] = data.loadout;
    const slots: [
      string,
      'C' | 'V',
      readonly ('cryo' | 'antibiotic' | 'fever' | 'immunity')[],
      string,
    ][] = [
      ['Слот C — лечение', 'C', ['cryo', 'antibiotic'], c],
      ['Слот V — атака или защита', 'V', ['fever', 'immunity'], v],
    ];
    slots.forEach(([label, key, kinds, current], row) => {
      const y = this.layout.slotRowY + row * this.layout.slotRowStep;
      const t = new Text({
        text: `${label} · клавиша ${key}`,
        style: textStyle({ size: 15, weight: '700' }),
      });
      t.position.set(28, y);
      card.addChild(t);
      const cw = (w - 56 - 16) / 2;
      kinds.forEach((kind, i) => {
        const selected = kind === current;
        const option = new Container();
        option.position.set(28 + i * (cw + 16), y + 32);
        const g = new Graphics();
        if (selected) {
          g.roundRect(-3, -3, cw + 6, this.layout.optionH + 6, 21).fill(COLOR.ink);
          g.roundRect(0, 0, cw, this.layout.optionH, 18)
            .fill(vertical(0x245678, 0x0e1c3a))
            .stroke({ width: 3, color: COLOR.mint });
        } else {
          g.roundRect(0, 0, cw, this.layout.optionH, 18)
            .fill({ color: COLOR.panelBottom, alpha: 0.75 })
            .stroke({ width: 2, color: COLOR.borderDim });
        }
        const ring = new Graphics()
          .circle(48, this.layout.optionH / 2, 32)
          .fill(vertical(0x2e6c9a, 0x11284a))
          .stroke({ width: 3, color: COLOR.ink });
        const icon = artSprite(`icon-ability-${kind}`, 34);
        icon.position.set(48, this.layout.optionH / 2);
        const name = new Text({
          text: ABILITY_NAMES[kind],
          style: textStyle({ size: 16, display: true, weight: '800' }),
        });
        name.position.set(96, this.layout.optionH / 2 - 40);
        const desc = new Text({
          text: DUEL_ABILITY_HINTS[kind],
          style: textStyle({ size: 13, color: COLOR.textSoft, wrap: cw - 110, lineHeight: 18 }),
        });
        desc.position.set(96, this.layout.optionH / 2 - 12);
        option.addChild(g, ring, icon, name, desc);
        option.eventMode = 'static';
        option.cursor = 'pointer';
        option.accessible = true;
        option.accessibleType = 'button';
        option.accessibleTitle = `${ABILITY_NAMES[kind]}${selected ? ' (выбрано)' : ''}`;
        option.on('pointertap', () => {
          if (row === 0) this.actions.selectLoadout(kind as 'cryo' | 'antibiotic', v);
          else this.actions.selectLoadout(c, kind as 'fever' | 'immunity');
        });
        card.addChild(option);
      });
    });
    const infect = new Text({
      text: 'Инфицирование работает всегда: цепочка ×2 и больше отправляет сопернику микробов.',
      style: textStyle({ size: 14, color: 0xffe08a, wrap: w - 56, lineHeight: 20 }),
    });
    infect.position.set(28, this.layout.infectY);
    card.addChild(infect);
  }

  private roleChoice(card: Container, data: LobbyData, me: PublicPlayer | null, w: number): void {
    const title = caps('Роль в команде');
    title.position.set(28, 28);
    card.addChild(title);
    const roles: [PlayerRole, string, number, string][] = [
      ['pilot', 'Пилот', COLOR.teal, 'role-pilot'],
      ['strategist', 'Стратег', COLOR.gold, 'role-strategist'],
    ];
    const cw = (w - 56 - 16) / 2;
    roles.forEach(([role, label, color, entryId], i) => {
      const holder = data.room.players.find((p) => p.role === role) ?? null;
      const mine = holder?.playerId === data.playerId;
      const taken = holder !== null && !mine;
      const option = new Container();
      option.position.set(28 + i * (cw + 16), 64);
      const g = new Graphics();
      if (mine) {
        g.roundRect(-3, -3, cw + 6, this.layout.roleH + 6, 21).fill(COLOR.ink);
        g.roundRect(0, 0, cw, this.layout.roleH, 18)
          .fill(vertical(0x245678, 0x0e1c3a))
          .stroke({ width: 3, color });
      } else {
        g.roundRect(0, 0, cw, this.layout.roleH, 18)
          .fill({ color: COLOR.panelBottom, alpha: 0.75 })
          .stroke({ width: 2, color: COLOR.borderDim });
      }
      const badge = caps(label, COLOR.ink, 13);
      const bw = badge.width + 20;
      const bg = new Graphics().roundRect(20, 20, bw, 30, 8).fill(color);
      badge.position.set(30, 27);
      const entry = data.catalog.find((e) => e.id === entryId);
      const desc = new Text({
        text: entry ? tr(entry.what) : '',
        style: textStyle({ size: 15, color: COLOR.textSoft, wrap: cw - 40, lineHeight: 22 }),
      });
      desc.position.set(20, 70);
      const kind = role === 'pilot' ? 'cryo' : 'scanner';
      const ring = new Graphics()
        .circle(46, this.layout.roleH - 120, 28)
        .fill(vertical(0x2e6c9a, 0x11284a))
        .stroke({ width: 3, color: COLOR.ink });
      const icon = artSprite(`icon-ability-${kind}`, 30);
      icon.position.set(46, this.layout.roleH - 120);
      const ability = new Text({
        text: `Способность: ${ABILITY_NAMES[kind]}`,
        style: textStyle({ size: 14, weight: '700', wrap: cw - 100 }),
      });
      ability.position.set(88, this.layout.roleH - 130);
      const who = new Text({
        text: mine
          ? 'Ваша роль'
          : taken
            ? `Занято: ${holder?.name ?? ''}`
            : 'Свободно — нажмите, чтобы выбрать',
        style: textStyle({
          size: 14,
          weight: '700',
          color: mine ? color : taken ? COLOR.textMuted : COLOR.textSoft,
        }),
      });
      who.position.set(20, this.layout.roleH - 44);
      option.addChild(g, bg, badge, desc, ring, icon, ability, who);
      if (taken) option.alpha = 0.6;
      option.eventMode = 'static';
      option.cursor = taken || !me ? 'default' : 'pointer';
      option.accessible = true;
      option.accessibleType = 'button';
      option.accessibleTitle = `${label}${mine ? ' (ваша роль)' : taken ? ' (занято)' : ''}`;
      option.on('pointertap', () => {
        if (!taken) this.actions.selectRole(role);
      });
      card.addChild(option);
    });
  }

  private countdown(seconds: number): void {
    const shade = new Graphics()
      .rect(-4000, -4000, 9440, 8900)
      .fill({ color: COLOR.night, alpha: 0.55 });
    const number = new Text({
      text: seconds > 0 ? String(seconds) : 'Старт!',
      style: textStyle({ size: 160, display: true, weight: '900', shadow: 8, color: COLOR.mint }),
    });
    number.anchor.set(0.5);
    number.position.set(this.layout.center.x, this.layout.center.y);
    const caption = new Text({
      text: 'Матч начинается',
      style: textStyle({ size: 22, display: true, weight: '800' }),
    });
    caption.anchor.set(0.5);
    caption.position.set(this.layout.center.x, this.layout.center.y + 130);
    this.addChild(shade, number, caption);
  }

  override destroy(): void {
    this.stops.splice(0).forEach((stop) => stop());
    super.destroy({ children: true });
  }
}
